/* ==========================================================
   SLAPCO MEGA MENU — assets/slapco-nav.js  v3.0
   Vanilla JS — no dependencies — no jQuery
   New in v3.0:
     • Search autocomplete (Shopify Predictive Search API)
     • Announcement bar slideshow with dot indicators
     • Resize handler (auto-close drawer at desktop width)
   ========================================================== */

(function () {
  'use strict';

  class MegaMenu {
    constructor(el) {
      this.el        = el;
      this.sectionId = el.dataset.sectionId || 'slapco-nav';

      /* ── Config from data attributes set by Liquid ────── */
      this.openMode     = (el.dataset.openMode     || 'hover').trim(); // 'hover' | 'click'
      this.slideDuration = parseInt(el.dataset.slideDuration || '5000', 10);

      /* ── Element references ─────────────────────────── */
      this.header            = el.querySelector('.slapco-nav__header');
      this.hamburger         = el.querySelector('.slapco-nav__hamburger');
      this.drawer            = el.querySelector('.slapco-nav__drawer');
      this.overlay           = el.querySelector('.slapco-nav__drawer-overlay');
      this.drawerClose       = el.querySelector('.slapco-nav__drawer-close');

      /* Announcements */
      this.announcements     = Array.from(el.querySelectorAll('.slapco-nav__announcement'));
      this.annDots           = Array.from(el.querySelectorAll('.slapco-nav__ann-dot'));
      this.announcementClose = el.querySelector('.slapco-nav__announcement-close');

      /* Search */
      this.searchToggle      = el.querySelector('[data-action="search"]');
      this.searchWrap        = el.querySelector('.slapco-nav__search-wrap');
      this.searchInput       = this.searchWrap && this.searchWrap.querySelector('.slapco-nav__search-input');
      this.searchSuggestions = this.searchWrap && this.searchWrap.querySelector('.slapco-nav__search-suggestions');

      /* Drawer search */
      this.drawerSearchSection = el.querySelector('.slapco-nav__drawer-search-section');
      this.drawerSearchInput   = this.drawerSearchSection && this.drawerSearchSection.querySelector('.slapco-nav__search-input');
      this.drawerSuggestions   = this.drawerSearchSection && this.drawerSearchSection.querySelector('.slapco-nav__search-suggestions');

      /* Desktop nav */
      this.desktopNavItems   = Array.from(el.querySelectorAll('.slapco-nav__nav-item--mega, .slapco-nav__nav-item--dropdown, .slapco-nav__nav-item--featured, .slapco-nav__nav-item--page-grid'));
      this.mobileAccordions  = Array.from(el.querySelectorAll('.slapco-nav__mobile-item--accordion'));

      /* ── State ──────────────────────────────────────── */
      this.hoverTimer        = null;
      this.leaveTimer        = null;
      this._swipeStartX      = 0;
      this._swipeStartY      = 0;
      this._focusTrapHandler = null;
      this._focusFirst       = null;
      this._focusLast        = null;
      this._annTimer         = null;
      this._annCurrent       = 0;
      this._resizeTimer      = null;

      this._init();
    }

    /* ────────────────────────────────────────────────────
       INIT
    ──────────────────────────────────────────────────── */
    _init() {
      this._initAnnouncement();
      this._initHamburger();
      this._initDesktopNav();
      this._initMobileAccordions();
      this._initSearch();
      this._initSearchAutocomplete();
      this._initKeyboard();
      this._initSwipe();
      this._initSticky();
      this._initResizeHandler();
      this._execCustomScripts();
    }

    /* ────────────────────────────────────────────────────
       CUSTOM SCRIPT RE-EXECUTOR
       Shopify's Theme Editor injects liquid blocks as HTML
       but <script> tags inside innerHTML are never executed
       by the browser. We clone + replace each script element
       to force execution, both on init and after section load.
    ──────────────────────────────────────────────────── */
    _execCustomScripts() {
      var selectors = [
        '.slapco-nav__panel-custom script',
        '.slapco-nav__custom-block-inline script',
        '.slapco-nav__nav-item--custom script'
      ].join(', ');

      var scripts = Array.from(this.el.querySelectorAll(selectors));
      scripts.forEach(function(oldScript) {
        var newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(function(attr) {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

    /* ────────────────────────────────────────────────────
       ANNOUNCEMENT BAR — dismiss + slideshow
    ──────────────────────────────────────────────────── */
    _initAnnouncement() {
      /* ── Dismiss button (closes all announcements) ── */
      if (this.announcementClose) {
        this.announcementClose.addEventListener('click', () => {
          this.announcements.forEach(a => { a.hidden = true; });
          var dotWrap = this.el.querySelector('.slapco-nav__ann-dots');
          if (dotWrap) dotWrap.hidden = true;
          this._stopSlideshow();
          try { sessionStorage.setItem('mm-ann-dismissed-' + this.sectionId, '1'); } catch (_) {}
        });

        /* Restore dismissed state */
        try {
          if (sessionStorage.getItem('mm-ann-dismissed-' + this.sectionId)) {
            this.announcements.forEach(a => { a.hidden = true; });
            var dotWrap = this.el.querySelector('.slapco-nav__ann-dots');
            if (dotWrap) dotWrap.hidden = true;
            return;
          }
        } catch (_) {}
      }

      /* ── Dot click navigation ── */
      this.annDots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          this._stopSlideshow();
          this._goToSlide(idx);
          this._startSlideshow();
        });
      });

      /* ── Start slideshow if more than one announcement ── */
      if (this.announcements.length > 1) {
        /* Ensure first slide is active */
        this._goToSlide(0);
        this._startSlideshow();
      }
    }

    _startSlideshow() {
      this._stopSlideshow();
      if (this.announcements.length <= 1) return;
      this._annTimer = setInterval(() => {
        var next = (this._annCurrent + 1) % this.announcements.length;
        this._goToSlide(next);
      }, this.slideDuration);
    }

    _stopSlideshow() {
      if (this._annTimer) {
        clearInterval(this._annTimer);
        this._annTimer = null;
      }
    }

    _goToSlide(idx) {
      /* Deactivate current */
      var prev = this.announcements[this._annCurrent];
      var prevDot = this.annDots[this._annCurrent];
      if (prev)    prev.classList.remove('slapco-nav__announcement--active');
      if (prevDot) prevDot.classList.remove('slapco-nav__ann-dot--active');

      /* Activate next */
      this._annCurrent = idx;
      var next = this.announcements[idx];
      var nextDot = this.annDots[idx];
      if (next)    next.classList.add('slapco-nav__announcement--active');
      if (nextDot) nextDot.classList.add('slapco-nav__ann-dot--active');
    }

    /* ────────────────────────────────────────────────────
       HAMBURGER / MOBILE DRAWER
    ──────────────────────────────────────────────────── */
    _initHamburger() {
      if (this.hamburger) {
        this.hamburger.addEventListener('click', () => this._openDrawer());
      }
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this._closeDrawer());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this._closeDrawer());
      }
    }

    _openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('mm-drawer-open');
      this.overlay && this.overlay.classList.add('mm-overlay-open');
      this.hamburger && this.hamburger.setAttribute('aria-expanded', 'true');
      this.drawer.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      this._trapFocus(this.drawer);
    }

    _closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('mm-drawer-open');
      this.overlay && this.overlay.classList.remove('mm-overlay-open');
      this.hamburger && this.hamburger.setAttribute('aria-expanded', 'false');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._releaseFocus();
    }

    /* ────────────────────────────────────────────────────
       FOCUS TRAP (mobile drawer)
    ──────────────────────────────────────────────────── */
    _trapFocus(container) {
      var focusable = Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.closest('[aria-hidden="true"]'));

      if (!focusable.length) return;

      this._focusFirst = focusable[0];
      this._focusLast  = focusable[focusable.length - 1];

      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === this._focusFirst) {
            e.preventDefault();
            this._focusLast.focus();
          }
        } else {
          if (document.activeElement === this._focusLast) {
            e.preventDefault();
            this._focusFirst.focus();
          }
        }
      };

      document.addEventListener('keydown', this._focusTrapHandler);
      setTimeout(() => { this._focusFirst && this._focusFirst.focus(); }, 60);
    }

    _releaseFocus() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
      this.hamburger && this.hamburger.focus();
    }

    /* ────────────────────────────────────────────────────
       DESKTOP NAV — mega panels & dropdowns
       Respects data-open-mode="hover|click"
    ──────────────────────────────────────────────────── */
    _initDesktopNav() {
      this.desktopNavItems.forEach((item) => {
        var trigger = item.querySelector('.slapco-nav__nav-link--trigger');
        var panel   = item.querySelector('.slapco-nav__panel, .slapco-nav__dropdown, .slapco-nav__page-grid-panel, .slapco-nav__featured-panel');
        if (!trigger || !panel) return;

        /* ── Hover mode (default) ── */
        if (this.openMode === 'hover') {
          item.addEventListener('mouseenter', () => {
            clearTimeout(this.leaveTimer);
            this.hoverTimer = setTimeout(() => {
              this._openDesktopPanel(trigger, panel);
            }, 80);
          });

          item.addEventListener('mouseleave', () => {
            clearTimeout(this.hoverTimer);
            this.leaveTimer = setTimeout(() => {
              this._closeDesktopPanel(trigger, panel);
            }, 120);
          });
        }

        /* ── Click toggle (always available for keyboard/touch) ── */
        trigger.addEventListener('click', (e) => {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            this._closeDesktopPanel(trigger, panel);
          } else {
            this._closeAllDesktopPanels();
            this._openDesktopPanel(trigger, panel);
          }
        });
      });

      /* Close on outside click */
      document.addEventListener('click', (e) => {
        if (!this.el.contains(e.target)) {
          this._closeAllDesktopPanels();
          this._closeSearch();
        }
      });
    }

    _openDesktopPanel(trigger, panel) {
      this._closeAllDesktopPanels();
      trigger.setAttribute('aria-expanded', 'true');
      panel.classList.add('mm-panel-open');
    }

    _closeDesktopPanel(trigger, panel) {
      trigger.setAttribute('aria-expanded', 'false');
      panel.classList.remove('mm-panel-open');
    }

    _closeAllDesktopPanels() {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      this.desktopNavItems.forEach((item) => {
        var trigger = item.querySelector('.slapco-nav__nav-link--trigger');
        var panel   = item.querySelector('.slapco-nav__panel, .slapco-nav__dropdown, .slapco-nav__page-grid-panel, .slapco-nav__featured-panel');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel)   panel.classList.remove('mm-panel-open');
      });
    }

    /* ────────────────────────────────────────────────────
       SEARCH — desktop inline expand
    ──────────────────────────────────────────────────── */
    _initSearch() {
      if (!this.searchToggle || !this.searchWrap) return;

      this.searchToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        var isOpen = this.searchWrap.classList.contains('mm-search-open');
        if (isOpen) {
          this._closeSearch();
        } else {
          this._openSearch();
        }
      });

      /* Prevent clicks inside the search wrap from closing it */
      this.searchWrap.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    _openSearch() {
      if (!this.searchWrap) return;
      this._closeAllDesktopPanels();
      this.searchWrap.classList.add('mm-search-open');
      this.searchToggle && this.searchToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        this.searchInput && this.searchInput.focus();
      }, 120);
    }

    _closeSearch() {
      if (!this.searchWrap) return;
      this.searchWrap.classList.remove('mm-search-open');
      this.searchToggle && this.searchToggle.setAttribute('aria-expanded', 'false');
      this._hideSuggestions(this.searchInput);
    }

    /* ────────────────────────────────────────────────────
       SEARCH AUTOCOMPLETE
       Works for both desktop search wrap and drawer search
    ──────────────────────────────────────────────────── */
    _initSearchAutocomplete() {
      /* Collect all search input / suggestions pairs */
      var pairs = [];

      if (this.searchInput && this.searchSuggestions) {
        pairs.push({ input: this.searchInput, suggestions: this.searchSuggestions });
      }
      if (this.drawerSearchInput && this.drawerSuggestions) {
        pairs.push({ input: this.drawerSearchInput, suggestions: this.drawerSuggestions });
      }

      pairs.forEach(({ input, suggestions }) => {
        var debounceTimer = null;
        var activeIndex   = -1;

        /* ── Typing ── */
        input.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          activeIndex = -1;
          var q = input.value.trim();
          if (q.length < 2) {
            this._hideSuggestionsEl(suggestions);
            return;
          }
          debounceTimer = setTimeout(() => {
            this._fetchSuggestions(q, input, suggestions);
          }, 300);
        });

        /* ── Keyboard navigation inside suggestions ── */
        input.addEventListener('keydown', (e) => {
          var items = Array.from(suggestions.querySelectorAll('.slapco-nav__suggestion-item'));
          if (!items.length) return;

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = Math.min(activeIndex + 1, items.length - 1);
            this._highlightSuggestion(items, activeIndex);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = Math.max(activeIndex - 1, -1);
            this._highlightSuggestion(items, activeIndex);
            if (activeIndex === -1) input.focus();
          } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            var link = items[activeIndex] && items[activeIndex].querySelector('a');
            if (link) link.click();
          } else if (e.key === 'Escape') {
            this._hideSuggestionsEl(suggestions);
            activeIndex = -1;
          }
        });

        /* ── Click outside to close ── */
        document.addEventListener('click', (e) => {
          if (!input.closest('form').contains(e.target)) {
            this._hideSuggestionsEl(suggestions);
          }
        });

        /* ── Clear on blur after short delay (allow click on suggestion) ── */
        input.addEventListener('blur', () => {
          setTimeout(() => {
            if (!suggestions.contains(document.activeElement)) {
              this._hideSuggestionsEl(suggestions);
            }
          }, 200);
        });
      });
    }

    _fetchSuggestions(q, input, suggestions) {
      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product'
        + '&resources[limit]=6';

      fetch(url)
        .then(function(r) { return r.json(); })
        .then((data) => {
          var products = (data
            && data.resources
            && data.resources.results
            && data.resources.results.products) || [];
          this._renderSuggestions(products, input, suggestions, q);
        })
        .catch(() => {
          this._hideSuggestionsEl(suggestions);
        });
    }

    _renderSuggestions(products, input, suggestions, q) {
      if (!products.length) {
        this._hideSuggestionsEl(suggestions);
        return;
      }

      var html = '<ul class="slapco-nav__suggestion-list" role="listbox" aria-label="Product suggestions">';

      products.forEach(function(product) {
        var title     = product.title || '';
        var url       = product.url   || '#';
        var imgSrc    = product.featured_image && product.featured_image.url
                          ? product.featured_image.url
                          : '';
        var price     = product.price_min != null
                          ? (window.Shopify && window.Shopify.formatMoney
                              ? window.Shopify.formatMoney(product.price_min)
                              : '$' + (product.price_min / 100).toFixed(2))
                          : '';

        /* Highlight the matched query in the title */
        var highlighted = title.replace(
          new RegExp('(' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi'),
          '<mark>$1</mark>'
        );

        html += '<li class="slapco-nav__suggestion-item" role="option">';
        html += '<a href="' + url + '" class="slapco-nav__suggestion-link">';
        if (imgSrc) {
          html += '<img src="' + imgSrc + '" alt="" class="slapco-nav__suggestion-img" width="40" height="40" loading="lazy">';
        }
        html += '<span class="slapco-nav__suggestion-text">';
        html += '<span class="slapco-nav__suggestion-title">' + highlighted + '</span>';
        if (price) {
          html += '<span class="slapco-nav__suggestion-price">' + price + '</span>';
        }
        html += '</span>';
        html += '</a>';
        html += '</li>';
      });

      /* "View all results" link */
      html += '<li class="slapco-nav__suggestion-item slapco-nav__suggestion-item--all">';
      html += '<a href="/search?q=' + encodeURIComponent(q) + '&type=product" class="slapco-nav__suggestion-link slapco-nav__suggestion-link--all">';
      html += 'View all results for <strong>' + this._escapeHtml(q) + '</strong>';
      html += '</a>';
      html += '</li>';

      html += '</ul>';

      suggestions.innerHTML = html;
      suggestions.classList.add('mm-suggestions-open');
    }

    _highlightSuggestion(items, idx) {
      items.forEach(function(item, i) {
        item.classList.toggle('slapco-nav__suggestion-item--active', i === idx);
        if (i === idx) {
          var link = item.querySelector('a');
          link && link.focus();
        }
      });
    }

    _hideSuggestions(input) {
      /* Find whichever suggestions panel belongs to this input */
      if (!input) return;
      var form = input.closest('form');
      if (!form) return;
      var sug = form.querySelector('.slapco-nav__search-suggestions');
      if (sug) this._hideSuggestionsEl(sug);
    }

    _hideSuggestionsEl(suggestions) {
      if (!suggestions) return;
      suggestions.classList.remove('mm-suggestions-open');
      suggestions.innerHTML = '';
    }

    _escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /* ────────────────────────────────────────────────────
       MOBILE ACCORDIONS
    ──────────────────────────────────────────────────── */
    _initMobileAccordions() {
      this.mobileAccordions.forEach((item) => {
        var trigger = item.querySelector('.slapco-nav__mobile-link--trigger');
        var submenu = item.querySelector('.slapco-nav__mobile-submenu');
        if (!trigger || !submenu) return;

        trigger.addEventListener('click', () => {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';

          /* Close all other accordions */
          this.mobileAccordions.forEach((other) => {
            if (other === item) return;
            var ot = other.querySelector('.slapco-nav__mobile-link--trigger');
            var os = other.querySelector('.slapco-nav__mobile-submenu');
            if (ot && ot.getAttribute('aria-expanded') === 'true') {
              ot.setAttribute('aria-expanded', 'false');
              if (os) os.style.maxHeight = '0';
            }
          });

          if (isOpen) {
            trigger.setAttribute('aria-expanded', 'false');
            submenu.style.maxHeight = '0';
          } else {
            trigger.setAttribute('aria-expanded', 'true');
            submenu.style.maxHeight = submenu.scrollHeight + 'px';
          }
        });
      });
    }

    /* ────────────────────────────────────────────────────
       KEYBOARD NAVIGATION
    ──────────────────────────────────────────────────── */
    _initKeyboard() {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;

        /* 1. Close search if open */
        if (this.searchWrap && this.searchWrap.classList.contains('mm-search-open')) {
          this._closeSearch();
          this.searchToggle && this.searchToggle.focus();
          return;
        }

        /* 2. Close mobile drawer if open */
        if (this.drawer && this.drawer.classList.contains('mm-drawer-open')) {
          this._closeDrawer();
          return;
        }

        /* 3. Close any open desktop panel */
        this._closeAllDesktopPanels();
      });
    }

    /* ────────────────────────────────────────────────────
       SWIPE TO CLOSE (touch)
    ──────────────────────────────────────────────────── */
    _initSwipe() {
      if (!this.drawer) return;

      this.drawer.addEventListener('touchstart', (e) => {
        this._swipeStartX = e.touches[0].clientX;
        this._swipeStartY = e.touches[0].clientY;
      }, { passive: true });

      this.drawer.addEventListener('touchend', (e) => {
        var dx = e.changedTouches[0].clientX - this._swipeStartX;
        var dy = Math.abs(e.changedTouches[0].clientY - this._swipeStartY);
        /* Swipe left ≥60px with <60px vertical drift → close */
        if (dx < -60 && dy < 60) {
          this._closeDrawer();
        }
      }, { passive: true });
    }

    /* ────────────────────────────────────────────────────
       STICKY HEADER
       IntersectionObserver on a sentinel element
    ──────────────────────────────────────────────────── */
    _initSticky() {
      if (!this.el.classList.contains('slapco-nav--sticky')) return;
      if (!window.IntersectionObserver) return;

      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
      this.el.insertAdjacentElement('beforebegin', sentinel);

      var observer = new IntersectionObserver(
        ([entry]) => {
          this.el.classList.toggle('slapco-nav--sticky-scrolled', !entry.isIntersecting);
        },
        { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
      );

      observer.observe(sentinel);
    }

    /* ────────────────────────────────────────────────────
       RESIZE HANDLER
       Auto-close mobile drawer when viewport widens to
       desktop breakpoint (≥1024px)
    ──────────────────────────────────────────────────── */
    _initResizeHandler() {
      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          if (window.innerWidth >= 1024) {
            /* Close drawer and restore scroll if it was open */
            if (this.drawer && this.drawer.classList.contains('mm-drawer-open')) {
              this._closeDrawer();
            }
            /* Also close any open search suggestions */
            this._hideSuggestionsEl(this.drawerSuggestions);
          }
        }, 150);
      }, { passive: true });
    }
  }

  /* ──────────────────────────────────────────────────────
     BOOTSTRAP
  ────────────────────────────────────────────────────── */
  function initAll() {
    document.querySelectorAll('[data-section-type="slapco-nav"]').forEach(function (el) {
      if (!el.dataset.mmInit) {
        el.dataset.mmInit = '1';
        new MegaMenu(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init on Shopify Theme Editor section load */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-section-type="slapco-nav"]');
    if (section) {
      delete section.dataset.mmInit;
      new MegaMenu(section);
    }
  });

})();
